import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import i18n, { type LangCode } from '@/i18n'

type Theme = 'light' | 'dark' | 'system'

interface SettingsState {
  lang: LangCode
  theme: Theme
  setLang: (lang: LangCode) => void
  setTheme: (theme: Theme) => void
}

const SYSTEM_DARK_MQ = '(prefers-color-scheme: dark)'

/** system 模式時 detect OS 偏好；明確指定就直接套用 */
function effectiveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'dark') return 'dark'
  if (theme === 'light') return 'light'
  return typeof window !== 'undefined' && window.matchMedia(SYSTEM_DARK_MQ).matches ? 'dark' : 'light'
}

/** 套用 theme 到 <html data-theme>。system 模式會 resolve 成 light/dark */
function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', effectiveTheme(theme))
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      lang: 'zh-TW',
      theme: 'system',
      setLang: (lang) => {
        i18n.changeLanguage(lang)
        set({ lang })
      },
      setTheme: (theme) => {
        applyTheme(theme)
        set({ theme })
      },
    }),
    {
      name: 'pixeltravel-settings',
      onRehydrateStorage: () => (state) => {
        if (!state) return
        applyTheme(state.theme)
        if (state.lang) i18n.changeLanguage(state.lang)
      },
    },
  ),
)

// 一次性監聽：user 選 system 時，OS dark/light 變化即時跟著切
if (typeof window !== 'undefined') {
  window.matchMedia(SYSTEM_DARK_MQ).addEventListener('change', () => {
    if (useSettingsStore.getState().theme === 'system') applyTheme('system')
  })
}
